import { publicApi } from "@api";
import GetCodeButton, { GetCodeButtonRef } from "@components/inputs/GetCodeButton/GetCodeButton";
import { envConfig } from "@configs/env";
import { OTPMethod, OTPSource, useStatsigTracking } from "@configs/statsig";
import { ValidatorSchema } from "@constants";
import { zodResolver } from "@hookform/resolvers/zod";
import useCaptureFormValidationErrors from "@hooks/useCaptureFormValidationErrors";
import useContactLinks from "@hooks/useContactLinks";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { getStoredCredential } from "@utils/credentials";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { getOTPCodeSentMessage } from "@utils/message";
import { formatTimer } from "@utils/misc";
import {
  APIErrorCodes,
  AuthToken,
  Button,
  FieldHelperText,
  LoginGrantType,
  TextField,
  cn,
  localizePhoneNumber,
  standardizePhoneNumber,
  useFieldHelperText,
  useHandleApiResponse,
} from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdMail, MdPhoneAndroid } from "react-icons/md";
import { z } from "zod";

interface FormPhoneAndOTP {
  onLoginSuccess?: (token?: AuthToken) => void;
  onForgotSecret?: () => void;
  onAccountLocked?: (isLocked: boolean) => void;
  isAccountLocked?: boolean;
  lockedRemainingTime?: number;
}

export type PhoneFormType = {
  phone: string;
  sms: string;
};

const inputClasses = {
  "input&": "w-full !px-0 !pl-11 text-start",
  "iconContainer&": "!left-3 !top-[10px]",
};

const FormPhoneAndOTP = ({
  onLoginSuccess,
  onForgotSecret,
  onAccountLocked,
  lockedRemainingTime,
  isAccountLocked,
}: FormPhoneAndOTP) => {
  const { t, i18n } = useTranslation();
  const { handleApiResponse } = useHandleApiResponse({ toast });
  const getCodeButtonRef = useRef<GetCodeButtonRef>(null);
  const [sendOTP, { isLoading: isSendingOTP }] = publicApi.useCreateForgotPasswordMutation();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = publicApi.useVerifyOTPMutation();
  const { phonePrefix, phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError, showPhoneInputPrefix } =
    useMemberSettings();
  const [countdownTime, setCountdownTime] = useState(0);
  const { trackOTPSent, trackOTPRequestFailed, trackOTPVerified, trackOTPFailedAttempt, trackOTPRequest } =
    useStatsigTracking();

  // Countdown timer effect
  useEffect(() => {
    if (isAccountLocked && countdownTime > 0) {
      const timer = setInterval(() => {
        setCountdownTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isAccountLocked, countdownTime]);

  // Update countdown time when lockedRemainingTime prop changes
  useEffect(() => {
    if (lockedRemainingTime !== undefined) {
      setCountdownTime(lockedRemainingTime);
    }
  }, [lockedRemainingTime]);

  const { openContact } = useContactLinks();

  const handleOpenContactLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  const getPersistedPhone = () => localizePhoneNumber(getStoredCredential().phone || "", phonePrefix);

  const phoneFormSchema = useMemo(() => {
    return z.object({
      phone: ValidatorSchema.phone({
        minLength: phoneMinLength,
        maxLength: phoneMaxLength,
        startWith: phoneStartWith,
        simpleError: phoneSimpleError,
        msgType: "digits",
      }),
      sms: ValidatorSchema.otp,
    });
  }, [phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError]);

  const {
    handleSubmit: handlePhoneSubmit,
    register: formPhoneRegister,
    trigger,
    watch,
    formState: { errors: formPhoneErrors, submitCount },
    getValues,
    setError,
    clearErrors,
  } = useForm<PhoneFormType>({
    mode: "onChange",
    resolver: zodResolver(phoneFormSchema),
    defaultValues: {
      phone: getPersistedPhone(),
      sms: "",
    },
  });

  const setPhoneError = useCallback(
    (error: string) => {
      if (error) {
        setError("phone", { message: error });
      } else {
        clearErrors("phone");
      }
    },
    [setError, clearErrors],
  );

  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess,
    setInlineError: setPhoneError,
    onForgotSecret,
    onAccountLocked,
  });

  // Capture form validation errors
  useCaptureFormValidationErrors(formPhoneErrors, {
    watch,
    feature: "login-phone-otp",
  });


  const fieldHelperTextManager = useFieldHelperText({
    submitCount: submitCount,
    trigger: trigger,
    errors: formPhoneErrors,
  });
  const handleGetCode = async () => {
    return new Promise((resolve, reject) => {
      trigger(["phone"]).then((isFormValid) => {
        if (!isFormValid) {
          reject(new Error("Form is not valid"));
          return;
        }
        const { phone } = getValues();
        trackOTPRequest(OTPMethod.SMS, { source: OTPSource.LOGIN });
        sendOTP({
          phone: standardizePhoneNumber(phone, phonePrefix),
          accountId: envConfig.accountId,
        }).then((response) => {
          handleApiResponse(response, {
            toastSuccess: false,
            toastError: false,
            onSuccess: (otpRes) => {
              toast.success(getOTPCodeSentMessage(otpRes.method, i18n.language, t));
              const nextRequestAt = otpRes.nextRequestAt;
              const remainingSeconds = moment(nextRequestAt).diff(moment(), "seconds");
              getCodeButtonRef.current?.startTimer(remainingSeconds);
              // Track OTP sent for login
              trackOTPSent(otpRes.method as OTPMethod, { source: OTPSource.LOGIN });
              resolve(otpRes);
            },
            onError: (error) => {
              if (error?.data?.error?.message === APIErrorCodes.Member.PhoneNotRegistered) {
                setError("phone", { message: "number not registered. please try another" });
                return;
              }
              const message = resolveAuthResponseError(error, t);
              if (message) {
                toast.error(message);
              }
              // Track OTP request failed for login
              trackOTPRequestFailed(error, { source: OTPSource.LOGIN });
              reject(error);
            },
          });
        });
      });
    });
  };

  const handleVerifyOTP = useCallback(
    async (code: string, phone: string) => {
      return new Promise((resolve, reject) => {
        verifyOTP({
          phone: standardizePhoneNumber(phone, phonePrefix),
          code: code,
          accountId: envConfig.accountId,
        }).then((response) => {
          handleApiResponse(response, {
            toastError: false,
            toastSuccess: false,
            onSuccess: (res) => {
              // Track OTP verified for login
              trackOTPVerified(OTPMethod.SMS, { source: OTPSource.LOGIN });
              resolve(res);
            },
            onError: (error) => {
              if (error?.data?.error?.message) {
                setError("sms", { message: error?.data?.error.message });
              }
              // Track OTP failed attempt for login
              trackOTPFailedAttempt(error, { source: OTPSource.LOGIN });
              reject(error?.data?.error?.message);
            },
          });
        });
      });
    },
    [handleApiResponse, verifyOTP, setError, phonePrefix, trackOTPVerified, trackOTPFailedAttempt],
  );

  const onSubmitPhone = async ({ phone, sms: code }: PhoneFormType) => {
    await handleVerifyOTP(code, phone);
    const standardizedPhone = standardizePhoneNumber(phone, phonePrefix);
    await handleLogin(standardizedPhone, code, LoginGrantType.OTP);
  };

  const isLoading = isVerifyingOTP || isLoginLoading;

  return (
    <form
      id="form-login-phone-otp"
      name="form-login-phone-otp"
      onSubmit={handlePhoneSubmit(onSubmitPhone)}
      className="flex w-full animate-fade-in flex-col gap-y-3"
      autoComplete="off"
    >
      <TextField
        {...formPhoneRegister("phone")}
        error={!!formPhoneErrors.phone}
        helperText={t(formPhoneErrors.phone?.message ?? "", {
          minLength: phoneMinLength,
          maxLength: phoneMaxLength,
          startWith: Array.isArray(phoneStartWith) ? phoneStartWith.join(", ") : "0",
        })}
        placeholder={t("phone number")}
        type={"number"}
        inputMode={"numeric"}
        icon={<MdPhoneAndroid className="text-2xl text-content-base" />}
        classes={{
          ...inputClasses,
          "input&": inputClasses["input&"] + (showPhoneInputPrefix ? " !pl-20" : ""),
          "prefixContainer&": showPhoneInputPrefix ? "absolute left-11 top-[10px] text-content-base" : "",
        }}
        size="sm"
        className="animate-fade-in"
        aria-label="Login phone input phone-otp"
        prefix={showPhoneInputPrefix ? phonePrefix : undefined}
        fieldHelperTextManager={fieldHelperTextManager}
      />
      <div className="relative">
        <TextField
          {...formPhoneRegister("sms")}
          error={!!formPhoneErrors.sms}
          helperText={t(formPhoneErrors.sms?.message as string)}
          placeholder={t("enter code")}
          type={"text"}
          icon={<MdMail className="text-2xl text-content-base" />}
          classes={inputClasses}
          size="sm"
          autoComplete="one-time-code"
          className="animate-fade-in"
          inputMode="numeric"
          aria-label="Login code input phone-otp"
          fieldHelperTextManager={fieldHelperTextManager}
        />
        <GetCodeButton
          onGetCode={handleGetCode}
          className="absolute right-3 top-[9px]"
          classes={{
            "root&": "w-fit px-2",
          }}
          loading={isSendingOTP}
          ref={getCodeButtonRef}
        />
        {formPhoneErrors.sms?.message === "otp is required. click on get code" && (
          <div className={cn(`text-right text-xs ${fieldHelperTextManager.shouldShowError("sms") ? "text-error-base" : "text-content-soft"}`)}>
            {t("didnt receive")} {""}
            <button
              onClick={handleOpenContactLink}
              className="underline"
              aria-label="Login contact cs phone-otp"
            >
              {t("contact cs")}
            </button>
          </div>
        )}
        {isAccountLocked && countdownTime > 0 && (
          <FieldHelperText
            error
            helperText={t("locked due to too many wrong tries. please try again in {{timer}}.", {
              timer: formatTimer(countdownTime, {
                minutes: false,
                seconds: true,
              }),
            })}
          />
        )}
      </div>
      <Button
        variant="primary"
        size="lg"
        type="submit"
        className="mt-2"
        disabled={isLoading}
        loading={isLoading}
      >
        {t("Login")}
      </Button>
    </form>
  );
};

export default FormPhoneAndOTP;
